import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { DocumentOwnerKind } from '@/entities/document'
import { entitySlotsQueryOptions } from '@/entities/slot'
import { uploadFile } from '@/shared/api'
import type { FileRef } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input, Textarea } from '@/shared/ui/field'
import { FileDrop } from '@/shared/ui/file-drop'
import { FileRow } from '@/shared/ui/file-row'
import { Select } from '@/shared/ui/select'
import { toast } from '@/shared/ui/toast'
import { useCreateDocument } from '../api/create'

interface CreateDocumentDialogProps {
  entityKind: DocumentOwnerKind
  entityId: string
  defaultSlotId?: string
  open: boolean
  onClose: () => void
}

export function CreateDocumentDialog({
  entityKind,
  entityId,
  defaultSlotId,
  open,
  onClose,
}: CreateDocumentDialogProps) {
  const slotsQ = useQuery(entitySlotsQueryOptions(entityKind, entityId))
  const create = useCreateDocument(entityKind, entityId)
  const [slotId, setSlotId] = useState(defaultSlotId ?? '')
  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [file, setFile] = useState<FileRef | null>(null)
  const [error, setError] = useState('')

  function submit() {
    if (!slotId) return setError('Выберите слот')
    if (!title.trim()) return setError('Укажите название')
    if (!file) return setError('Загрузите файл')
    setError('')
    create.mutate(
      { slot_id: slotId, title, file, ...(note.trim() ? { note } : {}) },
      {
        onSuccess: () => {
          toast.success('Документ добавлен')
          onClose()
        },
        onError: () => toast.error('Не удалось добавить документ'),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Новый документ"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={create.isPending || !file}>
            {create.isPending ? 'Добавляем…' : 'Добавить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Слот" {...(error === 'Выберите слот' ? { error } : {})}>
          {(props) => (
            <Select
              {...props}
              value={slotId}
              onChange={setSlotId}
              placeholder={slotsQ.isLoading ? 'Загрузка…' : 'Выберите слот'}
              options={(slotsQ.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
            />
          )}
        </Field>

        <Field label="Название" {...(error === 'Укажите название' ? { error } : {})}>
          {(props) => (
            <Input
              {...props}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Коммерческое предложение"
            />
          )}
        </Field>

        <div className="flex flex-col gap-2">
          <span className="label">Файл</span>
          {file ? (
            <FileRow file={file} onRemove={() => setFile(null)} />
          ) : (
            <FileDrop onUpload={uploadFile} onUploaded={setFile} hint="PDF, DOCX, XLSX до 50 МБ" />
          )}
          {error === 'Загрузите файл' && <span className="text-xs text-accent">{error}</span>}
        </div>

        <Field label="Заметка" hint="Необязательно">
          {(props) => (
            <Textarea
              {...props}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Что в этой версии"
            />
          )}
        </Field>
      </div>
    </Dialog>
  )
}
